import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpen, Dices, Plus, Skull, Zap, Bug } from "lucide-react";
import { toast } from "sonner";
import { HACKER_APPS, NANO_POWERS, rollBacklash, rollInfestation, rollRandom } from "@/lib/character";

const TABS = ["Apps", "Nano Powers", "Rolls"];

export default function Library({ open, onClose, character, onAdd, onNote }) {
  const [tab, setTab] = useState("Apps");
  const [lastRoll, setLastRoll] = useState(null);

  const list = tab === "Apps" ? HACKER_APPS : NANO_POWERS;

  const addToChar = (entry, kind) => {
    const field = kind === "app" ? "apps" : "nano_powers";
    const uuid = globalThis.crypto?.randomUUID?.() || `it_${Date.now()}`;
    const current = character?.[field] || [];
    onAdd({ [field]: [...current, { id: uuid, text: entry.text }] });
    toast.success(`${kind === "app" ? "APP" : "NANO"} ADDED · ${entry.text.split(" — ")[0]}`);
  };

  const rollRandomEntry = (kind) => {
    const pool = kind === "app" ? HACKER_APPS : NANO_POWERS;
    const chosen = rollRandom(pool);
    addToChar(chosen, kind);
  };

  const rollBack = () => {
    const r = rollBacklash();
    setLastRoll({ kind: "BACKLASH", roll: r.roll, effect: r.effect, die: "d20" });
    if (onNote) onNote(`[BACKLASH d20=${r.roll}] ${r.effect}`);
    toast.error(`BACKLASH d20=${r.roll}`, { description: r.effect });
  };
  const rollInf = () => {
    const r = rollInfestation();
    setLastRoll({ kind: "INFESTATION", roll: r.roll, effect: r.effect, die: "d20" });
    if (onNote) onNote(`[INFESTATION d20=${r.roll}] ${r.effect}`);
    toast(`INFESTATION d20=${r.roll}`, { description: r.effect });
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-cyan w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
            data-testid="library-modal"
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="flex items-center gap-3">
                <BookOpen className="text-[color:var(--accent-cyan)]" />
                <div className="font-display font-black uppercase tracking-tighter text-xl sm:text-2xl">LIBRARY//NET</div>
              </div>
              <button data-testid="library-close" onClick={onClose} aria-label="Close" className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]">
                <X size={18} />
              </button>
            </div>

            <div className="flex gap-2 px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)]">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`library-tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setTab(t)}
                  className={`px-3 py-2 border-2 text-xs font-bold uppercase tracking-widest ${tab === t ? "bg-[color:var(--accent-cyan)] text-[color:var(--bg-void)] border-[color:var(--accent-cyan)]" : "border-[color:var(--border-hard)] text-[color:var(--text-secondary)] hover:border-[color:var(--accent-cyan)]"}`}
                >
                  {t}
                </button>
              ))}
            </div>

            {tab !== "Rolls" ? (
              <>
                <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2 border-b-2 border-[color:var(--border-hard)]">
                  <button
                    data-testid={`library-random-${tab === "Apps" ? "app" : "nano"}`}
                    onClick={() => rollRandomEntry(tab === "Apps" ? "app" : "nano")}
                    className="cy-btn cy-btn-solid-yellow text-xs"
                  >
                    <Dices size={14} className="mr-1" /> ROLL RANDOM {tab === "Apps" ? "APP" : "NANO POWER"}
                  </button>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                    Tap ADD on any card to install it free.
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((entry, i) => {
                    const [name, ...rest] = entry.text.split(" — ");
                    return (
                      <div key={i} className="panel p-3 flex flex-col gap-2">
                        <div className="font-display font-black uppercase tracking-tight text-sm text-[color:var(--accent-yellow)]">{name}</div>
                        {rest.length > 0 && (
                          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] leading-snug">{rest.join(" — ")}</div>
                        )}
                        <button
                          data-testid={`library-add-${tab === "Apps" ? "app" : "nano"}-${i}`}
                          onClick={() => addToChar(entry, tab === "Apps" ? "app" : "nano")}
                          className="cy-btn cy-btn-cyan text-[10px] mt-auto"
                        >
                          <Plus size={12} className="mr-1" /> ADD FREE
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="panel panel-magenta p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Skull className="text-[color:var(--accent-magenta)]" />
                    <div className="font-display uppercase font-black tracking-tight text-lg">BACKLASH</div>
                  </div>
                  <div className="font-mono text-xs text-[color:var(--text-secondary)] mb-3 uppercase tracking-widest">
                    Pushed a Nano too hard? Roll d20.
                  </div>
                  <button data-testid="roll-backlash-btn" onClick={rollBack} className="cy-btn cy-btn-magenta w-full">
                    <Dices size={14} className="mr-2" /> ROLL BACKLASH d20
                  </button>
                </div>
                <div className="panel panel-cyan p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Bug className="text-[color:var(--accent-cyan)]" />
                    <div className="font-display uppercase font-black tracking-tight text-lg">INFESTATION</div>
                  </div>
                  <div className="font-mono text-xs text-[color:var(--text-secondary)] mb-3 uppercase tracking-widest">
                    Nano corruption bleeding into your body. Roll d20.
                  </div>
                  <button data-testid="roll-infestation-btn" onClick={rollInf} className="cy-btn cy-btn-cyan w-full">
                    <Dices size={14} className="mr-2" /> ROLL INFESTATION d20
                  </button>
                </div>
                {lastRoll && (
                  <div className="md:col-span-2 border-2 border-[color:var(--accent-yellow)] p-4" data-testid="library-roll-result">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                      {lastRoll.kind} · {lastRoll.die}=<span className="text-[color:var(--accent-yellow)] font-black">{lastRoll.roll}</span>
                    </div>
                    <div className="font-mono text-sm text-[color:var(--text-primary)] mt-1">{lastRoll.effect}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-2">
                      Appended to your NOTES field.
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
